import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Link,
  Menu,
  MenuItem,
  TextField,
  Stack,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderIcon from "@mui/icons-material/FolderOutlined";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMoveOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import ReportProblemIcon from "@mui/icons-material/ReportProblemOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import {
  assetInUseMessage,
  foyerApi,
  type AssetSummary,
  type MediaLayer,
  type PremiseSummary,
} from "../http/foyer";
import { useMediaScope, writeLayer } from "../context/PremiseScopeContext";
import { PermissionGate, usePermission, type PlaceRef } from "../auth/access";
import { tokens } from "../theme";
import { describeError, useToast } from "./toast";
import { Trans, useTranslation } from "react-i18next";
import { useFormat, type Formatters } from "../i18n/format";
import { translateKey, type Translate } from "../i18n/translate";
import MediaUploadDialog, { tomorrowISO } from "./MediaUploadDialog";
import { ItemFormSurface, type Dismiss } from "./EditDrawer";
import {
  dimensionLabel,
  durationLabel,
  matchesMediaFilters,
  orientationOf,
  purgeStateOf,
  joinsList,
  type MediaFilter,
  type ScreenOrientation,
} from "@shared/media";

interface Props {
  /**
   * When provided, clicking an asset calls this instead of opening a preview.
   * Picker-style. The whole asset rides along with the URL because a caller
   * building a playlist item needs the type, the poster and the clip length -
   * all of which it would otherwise have to guess from the file extension.
   */
  onSelect?: (url: string, asset: AssetSummary) => void;
  // Optional max-height for scroll containment in dialog usage.
  scrollContain?: boolean;
  /**
   * Show exactly one layer instead of the scope's natural view. The picker
   * passes this for its per-layer tabs; the Content page leaves it off so a
   * location sees its own files plus the ones inherited from its customer.
   */
  layer?: MediaLayer;
  /**
   * Picking for a list that is already one orientation (UNIT-1): files of the
   * other orientation are left out, since the save would refuse them. Files
   * nobody has measured stay, unlabelled.
   */
  fitsOrientation?: ScreenOrientation;
}

/**
 * The filter chips, in the order they read: what it is, then what shape.
 *
 * Orientation applies to stills as much as clips - a portrait totem needs
 * portrait photos too - so the shape options are not scoped to video.
 */
const FILTER_OPTIONS: MediaFilter[] = [
  "video",
  "image",
  "portrait",
  "landscape",
  "both",
];

function isImage(contentType: string): boolean {
  return contentType.startsWith("image/");
}

/**
 * A clip still being normalised for panels.
 *
 * 🚨 `panel: null` is NOT encoding. It means no transcode was ever asked for —
 * every image, and every clip uploaded before renditions existed. Those are
 * perfectly selectable and play their master, which is what happens today.
 * Treating absence as "in progress" would hide the entire back catalogue
 * behind a spinner that never resolves.
 */
export function isEncoding(asset: {
  panel?: { state: string } | null;
}): boolean {
  return asset.panel?.state === "queued" || asset.panel?.state === "running";
}

/**
 * How often to re-read while a clip is encoding.
 *
 * A transcode is minutes, so this is about a progress bar rather than a race —
 * five seconds is soon enough that "it finished" feels immediate and rare
 * enough that a long encode costs a handful of requests.
 */
const ENCODING_POLL_MS = 5_000;

function isVideo(contentType: string): boolean {
  return contentType.startsWith("video/");
}

function formatBytes(t: Translate, fmt: Formatters, n: number): string {
  const k = (unit: string, value: string | number) =>
    translateKey(t, "content:mediaGallery.bytes." + unit, { value });
  if (n < 1024) return k("b", n);
  if (n < 1024 * 1024)
    return k("kb", fmt.number(n / 1024, { maximumFractionDigits: 1 }));
  return k("mb", fmt.number(n / (1024 * 1024), { maximumFractionDigits: 1 }));
}

/** A purge date as the reader's calendar shows it, or null when there is none. */
function purgeDate(
  fmt: Formatters,
  purgeAt: string | Date | null | undefined
): string | null {
  if (!purgeAt) return null;
  const d = purgeAt instanceof Date ? purgeAt : new Date(purgeAt);
  if (Number.isNaN(d.getTime())) return null;
  return fmt.date(d);
}

/** Between a tile's facts: not a word, so not translated. */
const META_SEPARATOR = "  ·  ";
const LIGHTBOX_SEPARATOR = " · ";

const parentOf = (p: string) => p.split("/").slice(0, -1).join("/");
const nameOf = (p: string) => p.split("/").pop() ?? p;

/** Stable string identity for a layer, so effects re-run when it really moves
 *  rather than on every render of a freshly-built object. */
function layerKey(l: MediaLayer): string {
  if (l.kind === "premise") return `premise:${l.premiseId}`;
  if (l.kind === "company") return `company:${l.companyId}`;
  return "global";
}

export default function MediaGallery({
  onSelect,
  scrollContain,
  layer: forcedLayer,
  fitsOrientation,
}: Props) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const fmt = useFormat();
  // Where this gallery sits: a location, a customer, or neither (Global media).
  const scope = useMediaScope();

  // The layer every WRITE goes to. A forced layer (picker tab) wins; otherwise
  // it is the innermost thing in scope.
  const target: MediaLayer = forcedLayer ?? writeLayer(scope);
  // Only a location's natural view merges an inherited layer. A picker tab is
  // one layer and nothing else, so it never asks for the inherited files.
  const ownOnly = !!forcedLayer;
  const mediaPlace: PlaceRef =
    target.kind === "premise"
      ? { premiseId: target.premiseId }
      : target.kind === "company"
        ? { companyId: target.companyId }
        : "global";
  const writeLevel = target.kind === "global" ? "administrate" : "edit";
  const mayWrite = usePermission("media", writeLevel, mediaPlace);
  const mayRescope = usePermission("media", "administrate", "global");

  const [assets, setAssets] = useState<AssetSummary[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [cwd, setCwd] = useState(""); // current folder ("" = root)
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moveAnchor, setMoveAnchor] = useState<HTMLElement | null>(null);
  const [scopeAnchor, setScopeAnchor] = useState<HTMLElement | null>(null);
  /** The bulk purge-date modal, and the date being typed into it. */
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeDraft, setPurgeDraft] = useState("");
  /** The "New folder" modal. */
  const [folderOpen, setFolderOpen] = useState(false);
  const [premises, setPremises] = useState<PremiseSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  /**
   * Kind and shape filters, in one control.
   *
   * Multi-select because the questions combine: an operator hanging a portrait
   * totem wants portrait clips, which single-select forces them to find by
   * eye. Nothing selected means everything, so the common case needs no
   * interaction. See `matchesMediaFilters` for how the two axes compose.
   */
  const [filters, setFilters] = useState<MediaFilter[]>([]);
  /**
   * Shapes recovered in the browser for files the server never measured.
   *
   * Dimensions are recorded at upload, so anything from before that lands here
   * with no shape at all - invisible to the shape filters and unlabelled. The
   * image is already being fetched to draw the tile, so measuring it costs one
   * decode and nothing on the wire. Keyed by filename.
   */
  const [measured, setMeasured] = useState<
    Record<string, { width: number; height: number }>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AssetSummary | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * A file this view merely INHERITS - visible and usable, but owned by a
   * broader layer, so it must not offer delete/move here. Inside a location
   * that means its customer's shared files; on the Global media page an admin
   * owns everything they can see.
   */
  const isInherited = (a: AssetSummary) => a.scope !== target.kind;
  const canManage = (a: AssetSummary) =>
    mayWrite && (target.kind === "global" || !isInherited(a));

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [a, f] = await Promise.all([
        foyerApi.listAssets(target, { ownOnly }),
        foyerApi.listAssetFolders(target, { ownOnly }),
      ]);
      setAssets(a);
      setFolders(f);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
    // `target` is rebuilt each render; its identity is what actually varies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.kind, layerKey(target), ownOnly]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /**
   * Re-read while anything is still encoding, and stop the moment nothing is.
   *
   * 🚨 Without this the spinner is permanent. The list is fetched once, a
   * transcode finishes minutes later on the server, and the grid never hears
   * about it — so a clip that IS ready goes on looking busy and stays
   * unselectable until somebody reloads the page. That is a worse failure than
   * having no spinner at all, because it looks like the encode hung.
   *
   * Conditional on purpose: a library with nothing in flight makes no requests,
   * which is every library most of the time. `refresh` is deliberately out of
   * the deps — it changes identity with the scope, and including it would
   * restart the timer on every render.
   */
  const encodingCount = assets.filter(isEncoding).length;
  useEffect(() => {
    if (encodingCount === 0) return;
    const id = setInterval(() => {
      void refresh();
    }, ENCODING_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encodingCount]);

  /**
   * Measure images the server has no dimensions for.
   *
   * Only images: a clip cannot be sized without decoding it, which is the
   * uploader's job. Runs once per file, and a failure is silent - an
   * unmeasurable file simply stays unlabelled rather than blocking the grid.
   */
  useEffect(() => {
    const pending = assets.filter(
      (a) =>
        a.contentType.startsWith("image/") &&
        !a.orientation &&
        !measured[a.filename]
    );
    if (pending.length === 0) return;

    let alive = true;
    pending.forEach((a) => {
      const img = new Image();
      img.onload = () => {
        if (!alive || !img.naturalWidth || !img.naturalHeight) return;
        setMeasured((prev) =>
          prev[a.filename]
            ? prev
            : {
                ...prev,
                [a.filename]: {
                  width: img.naturalWidth,
                  height: img.naturalHeight,
                },
              }
        );
      };
      img.src = a.url;
    });
    return () => {
      alive = false;
    };
  }, [assets, measured]);

  /** An asset with any browser-recovered shape folded in. */
  const withShape = (a: AssetSummary): AssetSummary => {
    if (a.orientation || !measured[a.filename]) return a;
    const { width, height } = measured[a.filename]!;
    return { ...a, width, height, orientation: orientationOf(width, height) };
  };

  const childFolders = folders.filter((f) => parentOf(f) === cwd).sort();
  const filesInFolder = assets
    .map(withShape)
    .filter((a) => (a.folder || "") === cwd);
  const filesHere = filesInFolder.filter(
    (a) =>
      matchesMediaFilters(a, filters) &&
      (!fitsOrientation || joinsList(a.orientation, fitsOrientation))
  );
  /**
   * Files whose delete date has passed and that are still here (BE-29).
   *
   * Across the WHOLE library, not just the current folder: an unmet retention
   * obligation is not something an operator should have to go looking for one
   * folder at a time. Filters are ignored for the same reason.
   */
  const overdue = assets.filter((a) => purgeStateOf(a.purgeAt) === "overdue");
  const crumbs = cwd
    ? cwd.split("/").map((seg, i, arr) => ({
        label: seg,
        path: arr.slice(0, i + 1).join("/"),
      }))
    : [];

  const count = (n: number) => t("mediaGallery.fileCount", { count: n });
  const layerNoun = t(`mediaGallery.layer.${target.kind}`);
  const overdueNames = (rows: AssetSummary[]) => {
    const names = rows
      .slice(0, 4)
      .map((a) => a.originalName)
      .join(", ");
    return rows.length > 4
      ? t("mediaGallery.overdueNamesMore", { names, count: rows.length - 4 })
      : names;
  };

  function go(folder: string) {
    setCwd(folder);
    setSelected(new Set());
  }

  /** Creates `name` in the current folder; throws so the modal can say why. */
  async function newFolder(name: string) {
    await foyerApi.createAssetFolder(cwd ? `${cwd}/${name}` : name, target);
    toast.success(t("mediaGallery.folderCreated", { name }));
  }

  async function removeFolder(p: string) {
    if (
      !window.confirm(
        t("mediaGallery.deleteFolderConfirm", { name: nameOf(p) })
      )
    )
      return;
    try {
      await foyerApi.deleteAssetFolder(p, target);
      toast.success(t("mediaGallery.folderDeleted", { name: nameOf(p) }));
      await refresh();
    } catch {
      setError(t("mediaGallery.folderNotEmpty", { name: nameOf(p) }));
      toast.error(t("mediaGallery.folderNotEmptyToast", { name: nameOf(p) }));
    }
  }

  /**
   * One upload, measured in the browser first.
   *
   * The dialog has already refused anything oversized or of the wrong type, so
   * by the time this runs the only failure left is the network.
   */
  async function uploadOne(args: {
    file: File;
    name: string;
    width: number | null;
    height: number | null;
    durationSeconds: number | null;
    posterDataUrl: string | null;
    purgeAt: string | null;
  }) {
    setUploading(true);
    setError(null);
    try {
      const summary = await foyerApi.uploadAsset(args.file, cwd, target, {
        name: args.name,
        width: args.width,
        height: args.height,
        durationSeconds: args.durationSeconds,
        posterDataUrl: args.posterDataUrl,
        purgeAt: args.purgeAt,
      });
      setAssets((prev) => [summary, ...prev]);
      toast.success(
        t("mediaGallery.uploaded", {
          count: 1,
          name: args.name || args.file.name,
        })
      );
    } catch (err) {
      toast.error(
        describeError(t, "uploadFile", err, {
          name: args.name || args.file.name,
        })
      );
      throw err;
    } finally {
      setUploading(false);
    }
  }

  /**
   * The bare drag-and-drop / browse path: several files, no dialog, no meta.
   *
   * These uploads get NO PURGE DATE, deliberately and not by omission. There is
   * nowhere in this flow to make that decision, and inventing one - a house
   * default, or inheriting the last one used - would schedule a customer's file
   * for destruction on a date nobody chose. "No expiry" is the only honest
   * answer a path with no question can give. A date can be added afterwards
   * from the selection toolbar.
   */
  async function uploadFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    let done = 0;
    let current = "";
    try {
      for (const f of Array.from(files)) {
        current = f.name;
        const summary = await foyerApi.uploadAsset(f, cwd, target);
        setAssets((prev) => [summary, ...prev]);
        done += 1;
      }
      toast.success(t("mediaGallery.uploaded", { count: done, name: current }));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      toast.error(describeError(t, "uploadFile", err, { name: current }));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  // Re-scope the selected assets (admin only), served by the admin-only /scope
  // route - the one place that may move a file ACROSS layers. On the Global
  // media page that means pushing files down into a location; inside a
  // location it means promoting them to the whole company, or all the way up
  // to global.
  async function openScopeMenu(e: React.MouseEvent<HTMLElement>) {
    setScopeAnchor(e.currentTarget);
    if (target.kind === "global" && premises.length === 0) {
      try {
        setPremises(await foyerApi.listPremises());
      } catch {
        /* leave empty; the menu shows "no premises" */
      }
    }
  }

  async function setScopeFor(next: MediaLayer) {
    setScopeAnchor(null);
    const filenames = [...selected];
    if (!filenames.length) return;
    try {
      for (const f of filenames) await foyerApi.setAssetScope(f, next);
      setSelected(new Set());
      toast.success(
        t("mediaGallery.movedToLayer", {
          files: count(filenames.length),
          destination: t(`mediaGallery.destination.${next.kind}`),
        })
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      toast.error(describeError(t, "changeSelectedFilesScope", err));
    }
  }

  /**
   * Set (or clear, with `null`) the purge date on everything selected.
   *
   * Bulk because that is the shape of the real job: a seasonal campaign is a
   * dozen files that end on the same day, and setting the same date twelve
   * times is how one of them gets missed. It is also the answer to "does a
   * purge date belong on a FOLDER?" for now - selecting a folder's contents
   * covers the case without inventing a second place a date can live and then
   * having to say which one wins.
   */
  async function setPurgeForSelection(next: string | null) {
    const filenames = [...selected];
    if (!filenames.length) return;
    try {
      for (const f of filenames)
        await foyerApi.setAssetPurgeAt(f, next, target);
      setSelected(new Set());
      setPurgeDraft("");
      toast.success(
        next
          ? t("mediaGallery.purgeSet", {
              date: fmt.date(next),
              files: count(filenames.length),
            })
          : t("mediaGallery.purgeCleared", { files: count(filenames.length) })
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      toast.error(describeError(t, "setPurgeDate", err));
    }
  }

  function toggleSel(filename: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(filename)) n.delete(filename);
      else n.add(filename);
      return n;
    });
  }

  async function moveTo(folder: string) {
    setMoveAnchor(null);
    const filenames = [...selected];
    if (!filenames.length) return;
    try {
      await foyerApi.moveAssets(filenames, folder, target);
      setSelected(new Set());
      toast.success(
        folder
          ? t("mediaGallery.movedToFolder", {
              files: count(filenames.length),
              folder: nameOf(folder),
            })
          : t("mediaGallery.movedToTop", { files: count(filenames.length) })
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      toast.error(describeError(t, "moveSelectedFiles", err));
    }
  }

  /**
   * "Ej optimerad" -> try again. Flips the tile to the encoding spinner at
   * once, so the existing poll takes over and carries it to ready or failed.
   * On a refusal the server's state wins: re-read rather than guess.
   */
  async function retryPanel(filename: string) {
    setAssets((prev) =>
      prev.map((a) =>
        a.filename === filename && a.panel
          ? { ...a, panel: { ...a.panel, state: "queued", error: null } }
          : a
      )
    );
    try {
      await foyerApi.retryPanelTranscode(filename, target);
      toast.success(t("mediaGallery.restartStarted", { name: filename }));
    } catch (err) {
      const code = (err as { code?: string }).code;
      setError(
        code === "panel_not_failed"
          ? t("mediaGallery.alreadyPrepared")
          : t("mediaGallery.couldNotRestart", {
              reason: err instanceof Error ? err.message : String(err),
            })
      );
      toast.error(
        code === "panel_not_failed"
          ? t("mediaGallery.couldNotRestartNamed", { name: filename })
          : describeError(t, "restartOptimising", err, { name: filename })
      );
      await refresh();
    }
  }

  async function deleteAsset(filename: string) {
    if (!window.confirm(t("mediaGallery.deleteConfirm", { name: filename })))
      return;
    try {
      await foyerApi.deleteAsset(filename, target);
      setAssets((prev) => prev.filter((a) => a.filename !== filename));
      toast.success(t("mediaGallery.fileDeleted", { name: filename }));
    } catch (err) {
      // A file that is still on a screen comes back as a 409 naming what holds
      // it. Showing the raw code ("asset_in_use") would tell the operator they
      // failed without telling them what to do about it.
      const inUse = assetInUseMessage(t, err);
      setError(inUse ?? (err instanceof Error ? err.message : String(err)));
      toast.error(
        inUse
          ? t("mediaGallery.couldNotDeleteInUse", {
              name: filename,
              reason: inUse,
            })
          : describeError(t, "deleteFile", err, { name: filename })
      );
    }
  }

  return (
    <Stack spacing={2} data-testid="media-gallery">
      <MediaUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={uploadOne}
        destination={cwd || undefined}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Breadcrumbs aria-label={t("mediaGallery.folderPath")}>
          <Link
            component="button"
            underline={cwd ? "hover" : "none"}
            color={cwd ? "inherit" : "text.primary"}
            onClick={() => go("")}
            data-testid="crumb-root"
          >
            {layerNoun}
          </Link>
          {crumbs.map((c, i) => (
            <Link
              key={c.path}
              component="button"
              underline={i < crumbs.length - 1 ? "hover" : "none"}
              color={i < crumbs.length - 1 ? "inherit" : "text.primary"}
              onClick={() => go(c.path)}
            >
              {c.label}
            </Link>
          ))}
        </Breadcrumbs>
        <Stack direction="row" spacing={1}>
          {selected.size > 0 && (
            <Button
              size="small"
              startIcon={<DriveFileMoveIcon />}
              onClick={(e) => setMoveAnchor(e.currentTarget)}
              data-testid="media-move"
            >
              {t("mediaGallery.move", { count: selected.size })}
            </Button>
          )}
          {selected.size > 0 && (
            <Button
              size="small"
              startIcon={<ScheduleIcon />}
              onClick={() => setPurgeOpen(true)}
              data-testid="media-purge-date"
            >
              {t("mediaGallery.deleteDate", { count: selected.size })}
            </Button>
          )}
          {mayRescope && selected.size > 0 && (
            <Button
              size="small"
              startIcon={<SwapHorizIcon />}
              onClick={openScopeMenu}
              data-testid="media-scope"
            >
              {t("mediaGallery.moveTo")}
            </Button>
          )}
          <PermissionGate
            resource="media"
            level={writeLevel}
            place={mediaPlace}
          >
            <Button
              size="small"
              startIcon={<CreateNewFolderIcon />}
              onClick={() => setFolderOpen(true)}
              data-testid="media-new-folder"
            >
              {t("mediaGallery.newFolder")}
            </Button>
          </PermissionGate>
          <TextField
            select
            size="small"
            value={filters}
            onChange={(e) =>
              setFilters(
                // MUI hands a multiple select its value as an array already;
                // the string branch is only the autofill edge case.
                (typeof e.target.value === "string"
                  ? e.target.value.split(",")
                  : e.target.value) as MediaFilter[]
              )
            }
            sx={{ minWidth: 210 }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              // Nothing selected is the common case and means "everything", so
              // it says so rather than showing an empty box.
              renderValue: (v) => {
                const picked = v as MediaFilter[];
                if (picked.length === 0) return t("mediaGallery.filters.all");
                if (picked.length === 1)
                  return t(`mediaGallery.filters.${picked[0]!}`);
                return t("mediaGallery.filters.count", {
                  count: picked.length,
                });
              },
            }}
            data-testid="media-shape-filter"
          >
            {FILTER_OPTIONS.map((f) => (
              <MenuItem key={f} value={f} data-testid={`media-filter-${f}`}>
                <Checkbox
                  size="small"
                  checked={filters.includes(f)}
                  sx={{ mr: 0.5, p: 0.5 }}
                />
                {t(`mediaGallery.filters.${f}`)}
              </MenuItem>
            ))}
          </TextField>
          {filters.length > 0 && (
            <Button size="small" onClick={() => setFilters([])}>
              {t("common:buttons.clear")}
            </Button>
          )}
          <PermissionGate
            resource="media"
            level={writeLevel}
            place={mediaPlace}
          >
            <Button
              startIcon={<UploadFileIcon />}
              variant="contained"
              size="small"
              onClick={() => setUploadOpen(true)}
              disabled={uploading}
              data-testid="media-upload"
            >
              {uploading
                ? t("mediaGallery.uploading")
                : t("mediaGallery.uploadMedia")}
            </Button>
          </PermissionGate>
        </Stack>
        <input
          ref={fileInput}
          type="file"
          hidden
          multiple
          onChange={(e) => uploadFiles(e.target.files)}
          // Signage loops are clips, not stills. Narrowed to the containers a
          // TV WebView will actually decode rather than a blanket video/*.
          accept="image/*,video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov"
          data-testid="media-file-input"
        />
      </Stack>

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {/*
        Files past their delete date that are still here (BE-29).

        This is the surface the whole feature turns on. When the hourly sweep
        reaches a file whose date has passed and something still displays it,
        the deletion is REFUSED - a live reference beats a date typed months
        ago, and blanking a wall is an editorial decision nobody made. That
        refusal is correct AND it is a retention promise that has not been kept,
        so it has to be visible here rather than only in a container log. A
        purge that quietly never happens is worse than none, because it looks
        discharged.

        Derived, never stored: a row surviving past its own date IS the state.
      */}
      {overdue.length > 0 && (
        <Stack
          direction="row"
          alignItems="flex-start"
          gap={1.5}
          sx={{
            border: `1px solid ${tokens.warning}`,
            borderRadius: 1,
            p: 1.5,
          }}
          data-testid="media-purge-overdue"
        >
          <ReportProblemIcon
            sx={{ color: tokens.warning, fontSize: 20, mt: "2px" }}
          />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "fontWeightBold" }}>
              {t("mediaGallery.overdueTitle", { count: overdue.length })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("mediaGallery.overdueBody", {
                names: overdueNames(overdue),
              })}
            </Typography>
          </Box>
        </Stack>
      )}
      {loading && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {!loading && childFolders.length === 0 && filesHere.length === 0 && (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ py: 3, textAlign: "center" }}
          data-testid="media-empty"
        >
          <Trans
            i18nKey="mediaGallery.empty"
            t={t}
            components={{ strong: <strong /> }}
          />
        </Typography>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 2,
          ...(scrollContain ? { maxHeight: 480, overflowY: "auto" } : {}),
        }}
      >
        {/* Subfolders */}
        {childFolders.map((f) => (
          <Box
            key={f}
            data-testid={`folder-${f}`}
            onClick={() => go(f)}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 1,
              position: "relative",
              "&:hover": { bgcolor: "action.hover" },
              "&:hover .folder-delete": { opacity: 1 },
            }}
          >
            <FolderIcon color="action" />
            <Typography
              variant="body2"
              sx={{
                fontWeight: "fontWeightMedium",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={nameOf(f)}
            >
              {nameOf(f)}
            </Typography>
            <PermissionGate
              resource="media"
              level={writeLevel}
              place={mediaPlace}
            >
              <IconButton
                size="small"
                className="folder-delete"
                aria-label={t("mediaGallery.deleteFolderAria", {
                  name: nameOf(f),
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  removeFolder(f);
                }}
                sx={{ ml: "auto", opacity: 0, transition: "opacity 120ms" }}
                data-testid={`folder-delete-${f}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </PermissionGate>
          </Box>
        ))}

        {/* Files in this folder */}
        {filesHere.map((asset) => {
          const isSel = selected.has(asset.filename);
          return (
            <Box
              key={asset.filename}
              data-testid={`asset-${asset.filename}`}
              sx={{
                border: "2px solid",
                borderColor: isSel ? "primary.main" : "divider",
                borderRadius: 1,
                overflow: "hidden",
                cursor: "pointer",
                position: "relative",
                "&:hover .asset-delete": { opacity: 1 },
                "&:hover .asset-check": { opacity: 1 },
              }}
              /**
               * 🚨 Only PICKING waits. A clip mid-encode still opens in the
               * preview, still deletes, still moves — it is a real file with
               * real bytes. What it cannot do is get chosen for a wall, because
               * the rendition that makes it safe there does not exist yet.
               *
               * Blocking the whole tile would make an encoding clip look
               * broken rather than busy.
               */
              onClick={() => {
                if (onSelect && isEncoding(asset)) return;
                if (onSelect) onSelect(asset.url, asset);
                else setPreview(asset);
              }}
            >
              {canManage(asset) && (
                <Checkbox
                  size="small"
                  className="asset-check"
                  checked={isSel}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSel(asset.filename);
                  }}
                  sx={{
                    position: "absolute",
                    top: 2,
                    left: 2,
                    bgcolor: "rgba(255,255,255,0.85)",
                    borderRadius: 1,
                    p: 0.25,
                    opacity: isSel ? 1 : 0,
                    transition: "opacity 120ms",
                    "&:hover": { bgcolor: "white" },
                  }}
                  data-testid={`asset-check-${asset.filename}`}
                />
              )}
              {isInherited(asset) && (
                <Chip
                  label={
                    asset.scope === "company"
                      ? t("mediaGallery.inherited.company")
                      : t("mediaGallery.inherited.global")
                  }
                  size="small"
                  title={
                    asset.scope === "company"
                      ? t("mediaGallery.inheritedTitle.company")
                      : t("mediaGallery.inheritedTitle.global")
                  }
                  sx={{
                    position: "absolute",
                    top: 4,
                    left: 4,
                    height: 20,
                    typography: "micro",
                    bgcolor: "rgba(255,255,255,0.9)",
                  }}
                  data-testid={`asset-inherited-${asset.filename}`}
                />
              )}
              <Box
                sx={{
                  // `relative` so the type badge anchors to the THUMBNAIL. The
                  // card is itself positioned (for the delete button), so
                  // without this the badge resolved against the card and sat
                  // on top of the caption.
                  position: "relative",
                  height: 120,
                  bgcolor: "rgba(0,0,0,0.04)",
                  display: "grid",
                  placeItems: "center",
                  overflow: "hidden",
                }}
              >
                {isImage(asset.contentType) ? (
                  <img
                    src={asset.url}
                    alt={asset.originalName}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : isVideo(asset.contentType) && asset.posterUrl ? (
                  // A still captured at upload. Deterministic in every browser,
                  // and it costs neither a video decode nor the clip's bytes.
                  <img
                    src={asset.posterUrl}
                    alt={asset.originalName}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : isVideo(asset.contentType) ? (
                  // Clips uploaded before posters were captured. The `#t=0.1`
                  // fragment asks the browser to seek, which is what nudges it
                  // into painting a frame at all - without it some browsers
                  // leave a metadata-only video blank.
                  <video
                    src={`${asset.url}#t=0.1`}
                    preload="metadata"
                    muted
                    playsInline
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Stack alignItems="center" gap={0.5}>
                    <InsertDriveFileIcon
                      sx={{ fontSize: 40, color: "text.secondary" }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {t("mediaGallery.noPreview")}
                    </Typography>
                  </Stack>
                )}
                {/* Encoding, and what it means. A bare spinner reads as a
                    stuck upload; the word says the upload is DONE and
                    something else is happening. */}
                {isEncoding(asset) && (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    data-testid={`asset-encoding-${asset.filename}`}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      bgcolor: "rgba(15,23,42,0.62)",
                      color: "#fff",
                    }}
                  >
                    <CircularProgress size={22} sx={{ color: "#fff" }} />
                    <Typography
                      variant="micro"
                      sx={{ fontWeight: "fontWeightSemibold" }}
                    >
                      {t("mediaGallery.preparing")}
                    </Typography>
                  </Stack>
                )}
                {/* A rendition that failed. The clip is still usable — the
                    wall falls back to the master — so this warns rather than
                    blocks. Silently allowing it would be worse: an operator
                    would have no idea their clip may stall on a panel. */}
                {asset.panel?.state === "failed" && (
                  <Chip
                    size="small"
                    // The chip IS the retry where the operator owns the clip.
                    // It used to be a dead end: the only second attempt was
                    // deleting the clip and uploading it again.
                    label={
                      canManage(asset)
                        ? t("mediaGallery.notOptimisedRetry")
                        : t("mediaGallery.notOptimised")
                    }
                    data-testid={`asset-panel-failed-${asset.filename}`}
                    title={
                      asset.panel.error ?? t("mediaGallery.panelFailedTitle")
                    }
                    {...(canManage(asset)
                      ? {
                          onClick: (e: MouseEvent) => {
                            // The tile itself selects; this must not.
                            e.stopPropagation();
                            void retryPanel(asset.filename);
                          },
                          "aria-label": t("mediaGallery.retryAria", {
                            name: asset.originalName,
                          }),
                        }
                      : {})}
                    sx={{
                      position: "absolute",
                      bottom: 6,
                      right: 6,
                      height: 20,
                      typography: "micro",
                      fontWeight: "fontWeightBold",
                      bgcolor: tokens.warning,
                      color: "#fff",
                    }}
                  />
                )}
                {/* Type badge. A grid of stills and clips is unreadable
                    without it - a paused video and a photo look identical. */}
                <Chip
                  size="small"
                  label={
                    isVideo(asset.contentType)
                      ? t("mediaGallery.badge.video")
                      : t("mediaGallery.badge.image")
                  }
                  sx={{
                    position: "absolute",
                    bottom: 6,
                    left: 6,
                    height: 20,
                    typography: "micro",
                    fontWeight: "fontWeightBold",
                    letterSpacing: "0.4px",
                    bgcolor: "rgba(15,23,42,0.78)",
                    color: "#fff",
                  }}
                />
              </Box>
              <Box sx={{ p: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: "fontWeightMedium",
                  }}
                  title={asset.originalName}
                >
                  {asset.originalName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  {/* What an operator needs at a glance differs by kind: a
                      still is judged on its pixel size, a clip on how long it
                      holds the screen. Size is the fallback for anything
                      uploaded before shapes were measured. */}
                  {[
                    isVideo(asset.contentType)
                      ? durationLabel(asset.durationSeconds)
                      : dimensionLabel(asset.width, asset.height),
                    formatBytes(t, fmt, asset.size),
                  ]
                    .filter(Boolean)
                    .join(META_SEPARATOR)}
                </Typography>
                {asset.orientation && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: tokens.brand }}
                  >
                    {t(`mediaGallery.orientation.${asset.orientation}`)}
                  </Typography>
                )}
                {/* The delete date, on the tile rather than behind a click: a
                    file that disappears on a date is not a detail, and the
                    overdue case has to be findable in the grid it is sitting
                    in, not only in the banner that counts it. */}
                {/* Why it failed, readable without hovering. "The OSC token
                    service answered 403" tells an operator to call someone
                    rather than click again - but only if they can read it. */}
                {asset.panel?.state === "failed" && asset.panel.error && (
                  <Typography
                    variant="caption"
                    data-testid={`asset-panel-error-${asset.filename}`}
                    title={asset.panel.error}
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      color: tokens.warning,
                    }}
                  >
                    {asset.panel.error}
                  </Typography>
                )}
                {purgeStateOf(asset.purgeAt) === "scheduled" && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "text.secondary" }}
                    data-testid={`asset-purge-${asset.filename}`}
                  >
                    {t("mediaGallery.deletesOn", {
                      date: purgeDate(fmt, asset.purgeAt),
                    })}
                  </Typography>
                )}
                {purgeStateOf(asset.purgeAt) === "overdue" && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: tokens.warning,
                      fontWeight: "fontWeightBold",
                    }}
                    title={t("mediaGallery.overdueTileTitle")}
                    data-testid={`asset-purge-${asset.filename}`}
                  >
                    {t("mediaGallery.deleteOverdue", {
                      date: purgeDate(fmt, asset.purgeAt),
                    })}
                  </Typography>
                )}
              </Box>
              {canManage(asset) && (
                <IconButton
                  size="small"
                  className="asset-delete"
                  aria-label={t("mediaGallery.deleteAria", {
                    name: asset.filename,
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteAsset(asset.filename);
                  }}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    bgcolor: "rgba(255,255,255,0.85)",
                    opacity: 0,
                    transition: "opacity 120ms",
                    "&:hover": { bgcolor: "white" },
                  }}
                  data-testid={`asset-delete-${asset.filename}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Move-to menu */}
      <Menu
        anchorEl={moveAnchor}
        open={!!moveAnchor}
        onClose={() => setMoveAnchor(null)}
      >
        <MenuItem disabled dense>
          {t("mediaGallery.moveCountTo", { count: selected.size })}
        </MenuItem>
        {cwd !== "" && (
          <MenuItem onClick={() => moveTo("")} data-testid="move-to-root">
            {t("mediaGallery.layerRoot", { layer: layerNoun })}
          </MenuItem>
        )}
        {folders
          .filter((f) => f !== cwd)
          .map((f) => (
            <MenuItem key={f} onClick={() => moveTo(f)}>
              {f}
            </MenuItem>
          ))}
      </Menu>

      {/* Automatic delete date for the selection (BE-29). A date, and a way to
          take it away again - the second is what makes the first safe to use,
          since a purge date has no grace period once it passes. A bulk value
          set on several files at once, so a small modal, not a menu form. */}
      {purgeOpen && (
        <PurgeDateDialog
          count={selected.size}
          value={purgeDraft}
          onChange={setPurgeDraft}
          onApply={(next) => void setPurgeForSelection(next)}
          onClose={() => setPurgeOpen(false)}
        />
      )}

      {folderOpen && (
        <NewFolderDialog
          parent={cwd}
          onCreate={newFolder}
          onCreated={() => void refresh()}
          onClose={() => setFolderOpen(false)}
        />
      )}

      {/* Move-between-layers menu (admin only). Offers the layers that are
          actually reachable from here: everywhere can go global; a location
          can promote to its own customer; the Global media page can push a
          file down into a specific location. */}
      <Menu
        anchorEl={scopeAnchor}
        open={!!scopeAnchor}
        onClose={() => setScopeAnchor(null)}
      >
        <MenuItem disabled dense>
          {t("mediaGallery.moveCountTo", { count: selected.size })}
        </MenuItem>

        {target.kind !== "global" && (
          <MenuItem
            onClick={() => setScopeFor({ kind: "global" })}
            data-testid="scope-to-global"
          >
            {t("mediaGallery.layer.global")}
          </MenuItem>
        )}

        {scope.companyId && target.kind !== "company" && (
          <MenuItem
            onClick={() =>
              setScopeFor({ kind: "company", companyId: scope.companyId! })
            }
            data-testid="scope-to-company"
          >
            {t("mediaGallery.scopeMenu.thisCustomer")}
          </MenuItem>
        )}

        {target.kind === "global" && [
          <MenuItem disabled dense key="_hdr">
            {t("mediaGallery.scopeMenu.singleLocation")}
          </MenuItem>,
          ...(premises.length === 0
            ? [
                <MenuItem disabled dense key="_none">
                  {t("mediaGallery.scopeMenu.noLocations")}
                </MenuItem>,
              ]
            : premises.map((p) => (
                <MenuItem
                  key={p.premiseId}
                  onClick={() =>
                    setScopeFor({ kind: "premise", premiseId: p.premiseId })
                  }
                  data-testid={`scope-to-${p.premiseId}`}
                >
                  {p.premiseName}
                </MenuItem>
              ))),
        ]}
      </Menu>

      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        maxWidth="lg"
        data-testid="asset-lightbox"
      >
        {preview && (
          <Box
            sx={{
              position: "relative",
              bgcolor: "#000",
              display: "grid",
              placeItems: "center",
              minWidth: 320,
              minHeight: 240,
            }}
          >
            <IconButton
              onClick={() => setPreview(null)}
              aria-label={t("mediaGallery.lightbox.close")}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(0,0,0,0.5)",
                color: "white",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
              data-testid="lightbox-close"
            >
              <CloseIcon />
            </IconButton>
            {isImage(preview.contentType) ? (
              <img
                src={preview.url}
                alt={preview.originalName}
                style={{
                  display: "block",
                  maxWidth: "90vw",
                  maxHeight: "85vh",
                  objectFit: "contain",
                }}
              />
            ) : isVideo(preview.contentType) ? (
              // Controls on purpose: the reason to open a signage clip is to
              // check it, which means scrubbing it.
              <video
                src={preview.url}
                controls
                autoPlay
                muted
                playsInline
                style={{
                  display: "block",
                  maxWidth: "90vw",
                  maxHeight: "85vh",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Box sx={{ p: 6, color: "white", textAlign: "center" }}>
                <InsertDriveFileIcon sx={{ fontSize: 96 }} />
                <Typography sx={{ mt: 2 }}>{preview.originalName}</Typography>
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                bottom: 8,
                left: 12,
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {[
                preview.originalName,
                formatBytes(t, fmt, preview.size),
                purgeStateOf(preview.purgeAt) === "overdue"
                  ? t("mediaGallery.lightbox.overdueSince", {
                      date: purgeDate(fmt, preview.purgeAt),
                    })
                  : purgeStateOf(preview.purgeAt) === "scheduled"
                    ? t("mediaGallery.lightbox.deletes", {
                        date: purgeDate(fmt, preview.purgeAt),
                      })
                    : null,
              ]
                .filter(Boolean)
                .join(LIGHTBOX_SEPARATOR)}
            </Typography>
          </Box>
        )}
      </Dialog>
    </Stack>
  );
}

/** "New folder": a name, created inside the folder being viewed. */
function NewFolderDialog({
  parent,
  onCreate,
  onCreated,
  onClose,
}: {
  parent: string;
  onCreate: (name: string) => Promise<void>;
  onCreated: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The footer hands out `dismiss`; Enter in the name field needs it too.
  const dismissRef = useRef<Dismiss | null>(null);
  const trimmed = name.trim();

  async function create() {
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(trimmed);
      setBusy(false);
      dismissRef.current?.(() => {
        onCreated();
        onClose();
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      toast.error(describeError(t, "createFolder", err, { name: trimmed }));
      setBusy(false);
    }
  }

  return (
    <ItemFormSurface
      mode="create"
      onClose={onClose}
      title={t("mediaGallery.newFolderDialog.title")}
      subtitle={
        parent
          ? t("mediaGallery.newFolderDialog.inside", { parent })
          : undefined
      }
      maxWidth="xs"
      busy={busy}
      data-testid="new-folder-dialog"
      footerTop={
        error ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        ) : undefined
      }
      actions={(dismiss) => {
        dismissRef.current = dismiss;
        return (
          <>
            <Button onClick={() => dismiss()} disabled={busy}>
              {t("common:buttons.cancel")}
            </Button>
            <Button
              variant="contained"
              disabled={!trimmed || busy}
              onClick={() => void create()}
              data-testid="new-folder-create"
            >
              {t("common:buttons.create")}
            </Button>
          </>
        );
      }}
    >
      <TextField
        label={t("common:labels.name")}
        autoFocus
        fullWidth
        size="small"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void create();
          }
        }}
        sx={{ mt: 1 }}
        inputProps={{ "data-testid": "new-folder-name" }}
      />
    </ItemFormSurface>
  );
}

/**
 * The automatic delete date for a selection of files (BE-29): one date for
 * all of them, or "Never delete" to take it away again.
 */
function PurgeDateDialog({
  count,
  value,
  onChange,
  onApply,
  onClose,
}: {
  count: number;
  value: string;
  onChange: (next: string) => void;
  /** A date, or null for "Never delete". The page reports any failure. */
  onApply: (next: string | null) => void;
  onClose: () => void;
}) {
  // The selection is cleared once the write lands, which can be while this
  // fades out; the title keeps the count it opened with.
  const [n] = useState(count);
  const { t } = useTranslation(["content", "common"]);
  return (
    <ItemFormSurface
      mode="create"
      onClose={onClose}
      title={t("mediaGallery.purgeDateDialog.title")}
      subtitle={t("mediaGallery.purgeDateDialog.subtitle", { count: n })}
      maxWidth="xs"
      data-testid="purge-date-dialog"
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            onClick={() => {
              onApply(null);
              dismiss();
            }}
            data-testid="purge-date-clear"
          >
            {t("mediaGallery.purgeDateDialog.neverDelete")}
          </Button>
          <Button
            variant="contained"
            disabled={!value}
            onClick={() => {
              onApply(value);
              dismiss();
            }}
            data-testid="purge-date-apply"
          >
            {t("mediaGallery.purgeDateDialog.setDate")}
          </Button>
        </>
      )}
    >
      <TextField
        size="small"
        type="date"
        label={t("mediaGallery.purgeDateDialog.deleteOn")}
        fullWidth
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: tomorrowISO() }}
        sx={{ mt: 1 }}
        data-testid="purge-date-input"
      />
    </ItemFormSurface>
  );
}
